import { alertStateOf, type AlertCounts } from './markerStatus'

export const NO_ALERT_BLUE = '#487CCA'
export const ALERT_RED = '#EF4444'

const countLabel = (count: number) =>
  count > 999 ? `${Math.floor(count / 1000)}k+` : count.toString()

// The width of a single meter's pin, unselected.
export const MARKER_PIN_SIZE = 16

// Small enough that the map can show many groups side by side without
// merging them for overlap (see separateClusters).
const MIN_CLUSTER_SIZE = 24
export const MAX_CLUSTER_SIZE = 44

// Log scale, so small counts still show visible differences.
const sizeScale = (total: number) =>
  Math.min(Math.log(total) / Math.log(500), 1)

export const clusterIconSize = (total: number) =>
  Math.round(
    MIN_CLUSTER_SIZE + (MAX_CLUSTER_SIZE - MIN_CLUSTER_SIZE) * sizeScale(total)
  )

type ClusterIcon = {
  url: string
  scaledSize: google.maps.Size
  anchor: google.maps.Point
}

// Drawing an icon and encoding it costs about a millisecond, and a map shows
// thousands. The map also reloads a marker's image whenever it gets a new icon
// object, so the same counts must give the same object.
const iconCache = new Map<string, ClusterIcon>()
const ICON_CACHE_LIMIT = 5000

// count is the cached point count, shown until the cell has a status. With a
// status, the icon shows the status pair instead. A partial cluster is split in
// two: blue on the left, red on the right. The total sits large
// in the centre, and each half carries its own count small at its bottom. It
// has the same size as any other cluster of that total.
export const createClusterIcon = (count: number, alerts?: AlertCounts) => {
  if (typeof google === 'undefined' || !google.maps) return undefined

  const total = alerts?.pointCount ?? count
  const key = `${total}/${alerts?.alertCount ?? 0}`
  const cached = iconCache.get(key)
  if (cached) return cached
  const icon = drawClusterIcon(total, alerts)
  if (!icon) return undefined
  if (iconCache.size >= ICON_CACHE_LIMIT) iconCache.clear()
  iconCache.set(key, icon)
  return icon
}

const drawClusterIcon = (
  total: number,
  alerts?: AlertCounts
): ClusterIcon | undefined => {
  const state = alertStateOf(alerts)

  const size = clusterIconSize(total)
  const t = sizeScale(total)

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return undefined

  canvas.width = size
  canvas.height = size

  const centerX = size / 2
  const centerY = size / 2
  const radius = size / 2 - 3
  const fontSize = Math.round(12 + (16 - 12) * t)

  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  if (state === 'PARTIAL' && alerts) {
    const halves = [
      {
        count: alerts.pointCount - alerts.alertCount,
        x: centerX - radius * 0.42,
      },
      {
        count: alerts.alertCount,
        x: centerX + radius * 0.42,
      },
    ]

    // Two half fills leave an anti-aliased seam where their edges meet. A
    // full blue circle under the red half has no seam.
    ctx.beginPath()
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI)
    ctx.fillStyle = NO_ALERT_BLUE
    ctx.fill()

    ctx.beginPath()
    ctx.moveTo(centerX, centerY)
    ctx.arc(centerX, centerY, radius, -Math.PI / 2, Math.PI / 2)
    ctx.closePath()
    ctx.fillStyle = ALERT_RED
    ctx.fill()

    ctx.font = `bold ${fontSize}px Arial`
    ctx.fillStyle = 'white'
    ctx.fillText(countLabel(total), centerX, centerY - radius * 0.15)

    // Near its bottom a half is about 0.6 radius wide. A long number gets a
    // smaller font.
    const countY = centerY + radius * 0.5
    for (const half of halves) {
      const text = countLabel(half.count)
      let halfFont = Math.round(fontSize * 0.7)
      ctx.font = `bold ${halfFont}px Arial`
      while (halfFont > 7 && ctx.measureText(text).width > radius * 0.6) {
        halfFont -= 1
        ctx.font = `bold ${halfFont}px Arial`
      }
      ctx.fillText(text, half.x, countY)
    }
  } else {
    ctx.beginPath()
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI)
    ctx.fillStyle = state === 'FULL' ? ALERT_RED : NO_ALERT_BLUE
    ctx.fill()

    ctx.fillStyle = 'white'
    ctx.font = `bold ${fontSize}px Arial`
    ctx.fillText(countLabel(total), centerX, centerY)
  }

  // Draw border
  ctx.beginPath()
  ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI)
  ctx.strokeStyle = 'white'
  ctx.lineWidth = 2
  ctx.stroke()

  return {
    url: canvas.toDataURL(),
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(size / 2, size / 2),
  }
}
